import { lastValueFrom } from "rxjs";
import { createEffect, createResource, createSignal, For } from "solid-js";

import { DisksEditor } from "./DisksEditor";
import type { UtdoDisk } from "../types";
import { action } from "../util/action";
import { useApi } from "../util/api";
import { panic } from "../util/panic";
import { success } from "../util/success";

export function Disks() {
    const api = useApi();
    const [ currentId, setCurrentId ] = createSignal<number|undefined>(undefined);
    const [ disks, { refetch } ] = createResource(
        async () => {
            try {
                return await lastValueFrom(api.selectDisk());
            } catch (err: any) {
                panic(err);
                return [] as UtdoDisk[];
            }
        },
        { initialValue: [] as UtdoDisk[] },
    );

    const currentDisk = (): UtdoDisk => {
        const id = currentId();
        return disks().find((v) => v.id === id) ?? {
            id: 0,
            title: "",
            release: "",
            cover: "",
            hidden: true,
        };
    };

    let dialogRef!: HTMLDialogElement;

    createEffect(() => {
        if (currentId() === undefined) {
            dialogRef.close();
            return;
        }
        dialogRef.showModal();
    });

    const deleteDisk = action(
        async (c: UtdoDisk) => {
            if (!confirm(`Vous allez supprimer l'album "${c.title}".\nContinuer ?`)) {
                return;
            }
            await lastValueFrom(api.deleteDisk(c.id!));
        },
        refetch,
    );

    const editDisk = (id?: number) => (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        setCurrentId(id);
    };

    const save = async (disk: UtdoDisk) => {
        try {
            if ((disk.id ?? 0) > 0) {
                if (await lastValueFrom(api.updateDisk(disk))) {
                    success("Album enregistré avec succès.");
                }
            } else {
                if (await lastValueFrom(api.insertDisk(disk))) {
                    success("Album enregistré avec succès.");
                }
            }
            setCurrentId(undefined);
            refetch();
        } catch (err: any) {
            panic(err);
        }
    };

    const toggleVisiblity = action(
        async (disk: UtdoDisk) => lastValueFrom(api.toggleDiskVisibility(disk.id!)),
        refetch,
    );

    return <section id="admin-disks">
        <h2>Albums</h2>
        <div>
            <div class="actions">
                <button onClick={editDisk(0)}>Nouvel album</button>
            </div>
            <ul>
                <For each={disks()}>
                    {(disk) => <li>
                        <a href="#" onClick={editDisk(disk.id!)}>{disk.title}</a>
                        <div class="actions">
                            <a href="#" class="icon"
                                onClick={toggleVisiblity(disk)}>
                                {
                                    disk.hidden
                                        ? "visibility_off"
                                        : "visibility"
                                }
                            </a>
                            <a href="#" class="icon"
                                onClick={deleteDisk(disk)}>delete</a>
                        </div>
                    </li>}
                </For>
            </ul>
        </div>
        <dialog ref={dialogRef}>
            <DisksEditor
                disk={currentDisk()}
                validate={save}
                cancel={() => setCurrentId(undefined)}
                />
        </dialog>
    </section>;
}